import { isAuthorized, isOnlyInvestor as isOnlyInvestorFn, addAlert } from '../../../../utilities/helpers'
import { addBeaconNote, getBeaconHistory, markBatteryReplaced, searchBeacons } from '../../../../services/beacons-service'
import {
  EVENT_HEADERS,
  EVENT_PAGE_SIZE,
  LIST_HEADERS,
  dateTimeLocalMax,
  formatEvent,
  healthRows as buildHealthRows,
  identityRows as buildIdentityRows,
  historyParts,
  mergeBeaconDetail,
  presentBeacon,
  replacementInstant,
  shouldKeepEvents,
  shouldPageEvents
} from './beacon-detail'

const STATUS_OPTIONS = [
  { text: 'Not detected', value: 'not_detected' },
  { text: 'Low', value: 'low' },
  { text: 'OK', value: 'ok' },
  { text: 'Unknown', value: 'unknown' }
]

function eventPaginationState () {
  return {
    sortBy: 'sortWhen',
    descending: true,
    page: 1,
    rowsPerPage: EVENT_PAGE_SIZE
  }
}

export default {
  name: 'passengers-beacons',
  props: {
    signedInUser: Object
  },
  data () {
    return {
      apiInProgress: false,
      saving: false,
      selectedStatuses: [],
      protocol: '',
      manufacturer: '',
      searchKey: '',
      statusOptions: STATUS_OPTIONS,
      headers: LIST_HEADERS,
      eventHeaders: EVENT_HEADERS,
      eventPageSizes: [EVENT_PAGE_SIZE],
      records: [],
      total: 0,
      pagination: { sortBy: 'lastBarcodeSeenAt', descending: true, page: 1, rowsPerPage: 25 },
      detailOpen: false,
      detail: null,
      events: [],
      eventsOpen: false,
      eventsLoading: false,
      eventPagination: eventPaginationState(),
      note: '',
      replaceNote: '',
      replaceAt: '',
      replacementDateMax: dateTimeLocalMax(new Date())
    }
  },
  computed: {
    isAdmin () {
      return isAuthorized(this.signedInUser, 'admin')
    },
    isOnlyInvestor () {
      return isOnlyInvestorFn(this.signedInUser)
    },
    identityRows () {
      return buildIdentityRows(this.detail)
    },
    healthRows () {
      return buildHealthRows(this.detail)
    },
    showEventPager () {
      return shouldPageEvents(this.events)
    },
    replacementDateError () {
      const resolved = replacementInstant(this.replaceAt)
      if (!resolved || !resolved.error) return ''
      return resolved.error
    }
  },
  mounted () {
    this.refresh()
  },
  methods: {
    async refresh (setPage) {
      this.apiInProgress = true
      try {
        const pagination = this.pagination
        const page = setPage || pagination.page || 1
        const rowsPerPage = pagination.rowsPerPage || 25
        const offset = page === 1 ? 0 : (page * rowsPerPage) - rowsPerPage
        const order = pagination.sortBy
          ? pagination.sortBy + (pagination.descending ? ' DESC' : ' ASC')
          : 'lastBarcodeSeenAt DESC'
        const result = await searchBeacons({
          healthStatus: this.selectedStatuses,
          protocol: String(this.protocol || '').trim(),
          manufacturer: String(this.manufacturer || '').trim(),
          key: String(this.searchKey || '').trim(),
          order,
          limit: rowsPerPage,
          offset
        })
        const items = result && Array.isArray(result.items) ? result.items : []
        this.records = items.map(presentBeacon)
        this.total = result && result.total ? result.total : 0
      } catch (err) {
        addAlert({ message: err, type: 'error' })
      } finally {
        this.apiInProgress = false
      }
    },
    onPagination () {
      this.refresh()
    },
    openDetail (item) {
      return this.loadDetail(item)
    },
    async loadDetail (item, options) {
      const opts = options || {}
      const keepEvents = shouldKeepEvents(this.detail, item, opts.expandEvents)
      this.detailOpen = true
      this.eventsOpen = Boolean(opts.expandEvents)
      this.eventPagination = eventPaginationState()
      this.detail = presentBeacon(item)
      this.note = ''
      this.replaceNote = ''
      this.replaceAt = ''
      this.replacementDateMax = dateTimeLocalMax(new Date())
      if (!keepEvents) this.events = []
      this.eventsLoading = true
      try {
        const history = historyParts(await getBeaconHistory(item.id))
        this.detail = presentBeacon(mergeBeaconDetail(item, history.beacon))
        this.events = history.events.map(formatEvent)
      } catch (err) {
        addAlert({ message: err, type: 'error' })
      } finally {
        this.eventsLoading = false
      }
    },
    focusEvents () {
      this.$nextTick(() => {
        const card = this.$refs.eventsCard
        if (card && card.scrollIntoView) card.scrollIntoView({ block: 'nearest' })
      })
    },
    async submitNote () {
      if (!this.detail || !String(this.note || '').trim()) return
      this.saving = true
      try {
        await addBeaconNote(this.detail.id, String(this.note).trim())
        this.note = ''
        await this.loadDetail(this.detail, { expandEvents: true })
        this.focusEvents()
      } catch (err) {
        addAlert({ message: err, type: 'error' })
      } finally {
        this.saving = false
      }
    },
    refreshReplacementDateMax () {
      this.replacementDateMax = dateTimeLocalMax(new Date())
    },
    async submitReplacement () {
      if (!this.detail) return
      const resolved = replacementInstant(this.replaceAt)
      if (resolved && resolved.error) {
        addAlert({ message: resolved.error, type: 'error' })
        return
      }
      this.saving = true
      try {
        await markBatteryReplaced(
          this.detail.id,
          String(this.replaceNote || '').trim(),
          resolved ? resolved.at : undefined
        )
        this.replaceNote = ''
        this.replaceAt = ''
        await this.loadDetail(this.detail, { expandEvents: true })
        this.focusEvents()
        await this.refresh()
      } catch (err) {
        addAlert({ message: err, type: 'error' })
      } finally {
        this.saving = false
      }
    }
  }
}
